import { create } from 'zustand';
import type {
  Player,
  GambitState,
  GambitConfig,
  DareSubmission,
  RoundResult,
  GambitPlayerStats,
  GamePhase,
} from '@wasted-games/shared';

interface GameStore {
  // Connection
  sessionCode: string | null;
  playerId: string | null;
  isHost: boolean;
  connected: boolean;
  error: string | null;

  // Players
  players: Player[];

  // Game state
  phase: GamePhase;
  config: GambitConfig;
  currentRound: number;
  submissions: Record<string, DareSubmission>;
  boringVotes: Record<string, string>;
  excitingVotes: Record<string, string>;
  phaseStartTime: number | null;
  roundHistory: RoundResult[];
  playerStats: Record<string, GambitPlayerStats>;

  // Local UI state
  hasSubmitted: boolean;
  hasVotedBoring: boolean;
  hasVotedExciting: boolean;
  myBoringVote: string | null;
  myExcitingVote: string | null;

  // Winner info
  winner: Player | null;

  // Actions
  setConnection: (sessionCode: string, playerId: string, isHost: boolean) => void;
  setConnected: (connected: boolean) => void;
  setError: (error: string | null) => void;
  setPlayers: (players: Player[]) => void;
  addPlayer: (player: Player) => void;
  removePlayer: (playerId: string) => void;
  updateState: (state: GambitState) => void;
  setWinner: (winner: Player | null) => void;
  reset: () => void;
}

const initialState = {
  sessionCode: null,
  playerId: null,
  isHost: false,
  connected: false,
  error: null,
  players: [],
  phase: 'lobby' as GamePhase,
  config: {
    gameType: 'gambit' as const,
    targetPoints: 5,
    timeLimitSubmission: 60,
    timeLimitVoting: 30,
  },
  currentRound: 0,
  submissions: {},
  boringVotes: {},
  excitingVotes: {},
  phaseStartTime: null,
  roundHistory: [],
  playerStats: {},
  hasSubmitted: false,
  hasVotedBoring: false,
  hasVotedExciting: false,
  myBoringVote: null,
  myExcitingVote: null,
  winner: null,
};

export const useGameStore = create<GameStore>((set, get) => ({
  ...initialState,

  setConnection: (sessionCode, playerId, isHost) =>
    set({ sessionCode, playerId, isHost }),

  setConnected: (connected) => set({ connected }),

  setError: (error) => set({ error }),

  setPlayers: (players) => set({ players }),

  addPlayer: (player) =>
    set((state) => {
      if (state.players.find((p) => p.id === player.id)) return state;
      return { players: [...state.players, player] };
    }),

  removePlayer: (playerId) =>
    set((state) => ({
      players: state.players.filter((p) => p.id !== playerId),
    })),

  updateState: (gambitState) => {
    const { playerId } = get();

    // playerStats is now always a Record (not a Map)
    let statsRecord: Record<string, GambitPlayerStats> = {};
    if (gambitState.playerStats && typeof gambitState.playerStats === 'object') {
      // Handle both Map (shouldn't happen anymore) and Record
      if (gambitState.playerStats instanceof Map) {
        for (const [k, v] of gambitState.playerStats) {
          statsRecord[k] = v;
        }
      } else {
        statsRecord = gambitState.playerStats as Record<string, GambitPlayerStats>;
      }
    }

    // During submission phase, submissions are keyed by playerId.
    // During voting phase, submissions are keyed by submissionId (with own playerId preserved).
    const hasSubmitted = !!(
      playerId &&
      gambitState.submissions &&
      (gambitState.submissions[playerId] ||
        Object.values(gambitState.submissions).some((sub) => sub.playerId === playerId))
    );
    const hasVotedBoring = !!(playerId && gambitState.boringVotes && gambitState.boringVotes[playerId]);
    const hasVotedExciting = !!(playerId && gambitState.excitingVotes && gambitState.excitingVotes[playerId]);

    set({
      phase: gambitState.phase,
      config: gambitState.config,
      currentRound: gambitState.currentRound,
      submissions: gambitState.submissions || {},
      boringVotes: gambitState.boringVotes || {},
      excitingVotes: gambitState.excitingVotes || {},
      phaseStartTime: gambitState.phaseStartTime,
      roundHistory: gambitState.roundHistory || [],
      playerStats: statsRecord,
      hasSubmitted,
      hasVotedBoring,
      hasVotedExciting,
      myBoringVote: playerId ? (gambitState.boringVotes?.[playerId] || null) : null,
      myExcitingVote: playerId ? (gambitState.excitingVotes?.[playerId] || null) : null,
    });
  },

  setWinner: (winner) => set({ winner }),

  reset: () => set(initialState),
}));
